import { X, Download, Share } from "lucide-react";
import { useInstallPrompt } from "@/hooks/useInstallPrompt";
import { useLanguage } from "@/hooks/useLanguage";
import { cn } from "@/lib/utils";

const t = {
  fr: {
    title: "Installer Fibladi Play",
    description: "Installez l'application pour une meilleure experience",
    install: "Installer",
    iosInstructions: "Appuyez sur",
    iosThen: "puis \"Sur l'ecran d'accueil\"",
  },
  ar: {
    title: "تثبيت Fibladi Play",
    description: "ثبت التطبيق للحصول على تجربة أفضل",
    install: "تثبيت",
    iosInstructions: "اضغط على",
    iosThen: 'ثم "إضافة إلى الشاشة الرئيسية"',
  },
};

export function InstallPrompt() {
  const { isVisible, isIOS, canInstall, install, dismiss } = useInstallPrompt();
  const { language } = useLanguage();

  if (!isVisible) return null;

  const labels = t[language];

  return (
    <>
      {/* ── Mobile: centered overlay on top of video card ── */}
      <div className="fixed inset-0 z-[60] flex items-center justify-center lg:hidden animate-fade-in">
        {/* Backdrop */}
        <div className="absolute inset-0 bg-black/40" onClick={dismiss} />

        {/* Card */}
        <div className="relative flex flex-col items-center gap-4 rounded-2xl border border-border bg-card/95 backdrop-blur-md p-6 shadow-xl w-[min(80vw,20rem)] text-center animate-scale-in">
          {/* Close */}
          <button
            onClick={dismiss}
            className="absolute top-2.5 right-2.5 p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            aria-label="Close"
          >
            <X className="w-4 h-4" />
          </button>

          {/* App icon */}
          <img
            src="/fibladi-play.svg"
            alt="Fibladi Play"
            className="w-16 h-16 rounded-2xl"
          />

          {/* Text */}
          <div>
            <p className="text-base font-semibold text-foreground">
              {labels.title}
            </p>

            {isIOS ? (
              <p className="text-xs text-muted-foreground mt-1">
                {labels.iosInstructions}{" "}
                <Share className="inline w-3.5 h-3.5 -mt-0.5" />{" "}
                {labels.iosThen}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground mt-1">
                {labels.description}
              </p>
            )}
          </div>

          {/* Install button */}
          {canInstall && (
            <button
              onClick={install}
              className="flex items-center gap-2 px-5 py-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors w-full justify-center"
            >
              <Download className="w-4 h-4" />
              {labels.install}
            </button>
          )}
        </div>
      </div>

      {/* ── Desktop: top-right corner bar ── */}
      <div
        className={cn(
          "hidden lg:block fixed top-4   z-[60] max-w-sm animate-slide-down",
          language === "fr" ? "right-4" : "left-4",
        )}
      >
        <div className="relative flex items-center gap-3 rounded-xl border border-border bg-card/95 backdrop-blur-md p-3 shadow-lg">
          <img
            src="/fibladi-play.svg"
            alt="Fibladi Play"
            className="w-10 h-10 rounded-xl shrink-0"
          />

          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-foreground truncate">
              {labels.title}
            </p>
            {isIOS ? (
              <p className="text-xs text-muted-foreground mt-0.5">
                {labels.iosInstructions}{" "}
                <Share className="inline w-3.5 h-3.5 -mt-0.5" />{" "}
                {labels.iosThen}
              </p>
            ) : (
              <p className="text-xs text-muted-foreground mt-0.5">
                {labels.description}
              </p>
            )}
          </div>

          <div className="flex items-center gap-2 shrink-0">
            {canInstall && (
              <button
                onClick={install}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 transition-colors"
              >
                <Download className="w-4 h-4" />
                {labels.install}
              </button>
            )}

            <button
              onClick={dismiss}
              className="p-1 rounded-md text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
